package com.example.mystybag.login.logic

import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.BaseViewModel
import com.example.mystybag.core.logic.base.MainCallback
import com.example.mystybag.core.logic.base.NavigatorConfig
import com.example.mystybag.core.logic.base.ParentCallback
import com.example.mystybag.core.logic.base.ParentViewModel
import com.example.mystybag.core.logic.base.UIBottomSheetConfig
import com.example.mystybag.common.logic.InternetTroubleshootingViewModel
import com.example.mystybag.common.logic.NavigatorEnterMailAddress
import com.example.mystybag.common.logic.NavigatorInternetTroubleshooting
import com.example.mystybag.common.logic.NavigatorChooseCity
import com.example.mystybag.common.logic.NavigatorVerifyEmailAddress
import com.example.mystybag.cropImage.logic.NavigatorProfileCropImage
import com.example.mystybag.cropImage.logic.ProfileImageCropperViewModel
import com.example.mystybag.url.logic.NavigatorURLView
import com.example.mystybag.url.logic.URLViewViewModel
import com.example.mystybag.common.logic.NavigatorMerchantLogin
import com.example.mystybag.common.logic.NavigatorMerchantBiometric
import com.example.mystybag.common.logic.NavigatorMerchantForgotPasswordEmail
import com.example.mystybag.common.logic.NavigatorMerchantResetPassword
import com.example.mystybag.common.logic.NavigatorStaffLogin
import com.example.mystybag.common.logic.NavigatorStaffBiometric
import com.example.mystybag.common.logic.NavigatorStaffForgotPasswordEmail
import com.example.mystybag.common.logic.NavigatorStaffResetPassword
import com.example.mystybag.common.logic.NavigatorStaffChangePassword

class FeatureLoginAndRegistrationViewModel(context: AppContext, config: NavigatorConfig? = null): ParentViewModel(
    context,
    (config as? NavigatorFeatureLoginAndRegistration)?.initialChild
        ?: (config as? NavigatorAddLocationPrompt)
        ?: (config as? NavigatorAddFirstBagPrompt)
        ?: getRequiredNavigation()
),
    LoginInterface,
    MainCallback
{
    override fun getStackKey(): String = "FeatureLoginAndRegistrationStackKey"

    /**
     * [appChildStack] passes each child [ParentCallback] from [getCallbacks]. The default was `this`,
     * so [parentOpenBottomSheet] opened sheets on this VM's slot — but [AppView]'s
     * [BottomSheetLayoutView] is wired only to the root [MystyBagDigitalWalletViewModel] sheet.
     * Sheets from login (language picker, etc.) never appeared on Web/Desktop.
     *
     * Forward sheet open/close to [parentCallbacks] (app root) when present; keep [goBack] here.
     * The returned object must also implement [LoginInterface] and [MainCallback]: children call
     * [setResult] with those types and [kotlin.reflect.safeCast]; a plain [ParentCallback] only
     * would drop successful login navigation (merchant/client after API 200).
     */
    override fun getCallbacks(config: NavigatorConfig): ParentCallback {
        val appRoot = parentCallbacks
        val vm = this@FeatureLoginAndRegistrationViewModel
        return object : ParentCallback,
            LoginInterface by vm,
            MainCallback by vm {
            override fun parentOpenBottomSheet(cfg: UIBottomSheetConfig) {
                if (appRoot != null) {
                    appRoot.parentOpenBottomSheet(cfg)
                } else {
                    openBottomSheet(cfg)
                }
            }

            override fun parentCloseBottomSheet() {
                if (appRoot != null) {
                    appRoot.parentCloseBottomSheet()
                } else {
                    closeBottomSheet()
                }
            }

            override fun goBack(onComplete: ((Boolean) -> Unit)?) {
                vm.goBack(onComplete)
            }
        }
    }

    override fun popToMainAndNavigate(navigatorConfig: NavigatorConfig?) {
        // Delegate to parent MainCallback
        setResult<MainCallback> {
            popToMainAndNavigate(navigatorConfig)
        }
    }
    
    fun navigateToDashboardAndReload() {
        // Navigate to Dashboard and reload data
        setResult<MainCallback> {
            popToMainAndNavigate(NavigatorReturnToDashboard)
        }
    }

    override fun createViewModel(config: NavigatorConfig, context: AppContext): BaseViewModel {
        return when (config) {
            is NavigatorWelcome                 -> WelcomeViewModel(context, fromBackButton = false)
            is NavigatorWelcomeFromBack         -> WelcomeViewModel(context, fromBackButton = true)
            is NavigatorProfileCropImage         -> ProfileImageCropperViewModel(
                context,
                config.selectedImage,
                config.callbacks,
                config.aspectRatio,
                config.outputWidth,
                config.outputHeight,
            )
            is NavigatorURLView                 -> URLViewViewModel(context, config.url, config.title)
            is NavigatorInternetTroubleshooting -> InternetTroubleshootingViewModel(context)
            is NavigatorChooseCity                  -> ChooseCityViewModel(
                context,
                config.userId,
                config.email,
                config.credentialTypes,
                config.firstName,
                config.lastName,
                config.photoUrl,
            )
            is NavigatorVerifyEmailAddress          -> VerifyEmailAddressViewModel(context, config.userId, config.email, config.cityId)
            is NavigatorEnterMailAddress            -> EnterEmailViewModel(context)
            
            // Merchant Login
            is NavigatorMerchantLogin              -> MerchantLoginViewModel(context)
            is NavigatorMerchantBiometric          -> MerchantBiometricViewModel(context, config)
            is NavigatorMerchantForgotPasswordEmail -> MerchantForgotPasswordEmailViewModel(context)
            is NavigatorMerchantResetPassword      -> MerchantResetPasswordViewModel(context, config)

            // Staff (employee) login flow
            is NavigatorStaffLogin              -> StaffLoginViewModel(context)
            is NavigatorStaffBiometric          -> StaffBiometricViewModel(context, config)
            is NavigatorStaffForgotPasswordEmail -> StaffForgotPasswordEmailViewModel(context)
            is NavigatorStaffResetPassword      -> StaffResetPasswordViewModel(context, config)
            is NavigatorStaffChangePassword     -> StaffChangePasswordViewModel(context)

            // ========== СЪБИРАНЕ НА ДАННИ ЗА ТЪРГОВЕЦА ==========
            // Step 1: Add company details (name, country, city, street)
            is NavigatorMerchantRegistrationStep1 -> MerchantRegistrationAddCompanyDetailsViewModel(context, config)
            // Step 1B: Add company contact (phone number and privacy policy)
            is NavigatorMerchantRegistrationStep1B -> MerchantRegistrationAddCompanyContactViewModel(context, config)
            // Step 2: Add registered company address
            is NavigatorMerchantRegistrationStep2 -> MerchantRegistrationAddCompanyAddressViewModel(context, config)
            // Add/Edit MOL (separate screen)
            is NavigatorAddMol -> AddMolViewModel(context, config)
            // Step 3: Payment Data
            is NavigatorMerchantRegistrationStep3 -> MerchantRegistrationPaymentDataViewModel(context, config)
            
            // ========== СЪБИРАНЕ НА ДАННИ ЗА ЛОГИН ==========
            // Step 4: Add login details
            is NavigatorMerchantRegistrationStep4 -> MerchantRegistrationAddLoginDetailsViewModel(context, config)
            // Step 5: Check your Inbox
            is NavigatorMerchantRegistrationStep5 -> MerchantRegistrationCheckInboxViewModel(context, config)
            // Success screen after merchant registration
            is NavigatorMerchantRegistrationSuccess -> MerchantRegistrationSuccessViewModel(context)
            // Informational screen shown when merchant registration is still under review
            is NavigatorMerchantRegistrationUnderReview -> MerchantRegistrationUnderReviewViewModel(context)
            // Prompt to add location (shown when merchant has no locations)
            is NavigatorAddLocationPrompt -> AddLocationPromptViewModel(context, config)
            // Prompt to add first bag (shown after location creation)
            is NavigatorAddFirstBagPrompt -> AddFirstBagPromptViewModel(context, config)
            
             else                                   -> super.createViewModel(config, context)
        }
    }

    override fun goToMainScreen(showProgress: Boolean, onErrorCallback: (() -> Unit)?, target: NavigatorConfig?) {
        setResult<LoginInterface> { goToMainScreen(showProgress, onErrorCallback, target) }
    }
}

interface LoginInterface {
    fun goToMainScreen(showProgress: Boolean, onErrorCallback: (() -> Unit)? = null, target: NavigatorConfig? = null)
}

private fun getRequiredNavigation(): NavigatorConfig {
    //getApplicationsConfig( request = GetApplicationsConfigRequestModel(walletID = getWalletIDValue()) )


       // NavigatorSplash(isLinksNeeded, isRegulaNeeded)

        //NavigatorPasscode(LoginType.SHORT_LOGIN)
    //else
      return  NavigatorWelcome
}